import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { TableNames } from "../../../enums/tableNames";
import { RootState } from "../../../redux/store/store";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { buildOncologySubmissionFilterPayload } from "../utils/buildOncologySubmissionFilterPayload";
import { OncologySubmissionTableState } from "./useOncologySubmissionTableData";

export interface UseInitialDataFetchingForTableReturn {
  initialDataFetchingExecuted: boolean;
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useInitialDataFetchingForTable = (
  setOncologySubmissionState: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number,
): UseInitialDataFetchingForTableReturn => {
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState<boolean>(false);

  const tableAttributes = getTableDataFromContext(
    TableNames.ONCOLOGY_SUBMISSION_ORDERS,
  );

  const { filters } = useSelector((state: RootState) => state.filterValues);

  const validFilters = useMemo(() => {
    return filters.filter(
      (filter) => filter.name === TableNames.ONCOLOGY_SUBMISSION_ORDERS,
    );
  }, [filters]);

  const setOncologySubmissionStateRef = useRef(setOncologySubmissionState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    setOncologySubmissionStateRef.current = setOncologySubmissionState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [setOncologySubmissionState, setTableData, setTableDataForContext]);

  useEffect(() => {
    if (!initialDataFetchingExecuted && dateFromStart && dateFromEnd) {
      const searchedText = tableAttributes?.searchedText ?? "";

      const payload = buildOncologySubmissionFilterPayload(
        validFilters,
        searchedText,
        dateFromStart,
        dateFromEnd,
        pageSize,
      );

      makePostCallForInitialDataFetch(
        payload,
        setOncologySubmissionStateRef.current,
        setTableDataRef.current,
      ).then(() => {
        setInitialDataFetchingExecuted(true);

        setTableDataForContextRef.current(
          TableNames.ONCOLOGY_SUBMISSION_ORDERS,
          {
            lastBatchFetched: 1,
          },
        );
      });
    }
  }, [
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
    tableAttributes?.searchedText,
    validFilters,
  ]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
